import styled, { css } from 'styled-components';
import { Formik, Form, FormikProps, FormikValues } from 'formik';
import { useEffect, useRef, useState } from 'react';

import { Button } from 'shared/components/Button';
import { FitFeedbackFormApparel } from 'productTemplate/components/fitFeedback/FitFeedbackFormApparelDeprecated';
import { FitFeedbackFormSneakers } from 'productTemplate/components/fitFeedback/FitFeedbackFormSneakersDeprecated';
import { FitFeedbackFormUserInfo } from 'productTemplate/components/fitFeedback/FitFeedbackFormUserInfo';
import { getFitFeedbackFormValidationSchema } from 'productTemplate/utils/getFitFeedbackFormValidationSchema';
import {
  INITIAL_APPAREL_FIT_FEEDBACK_FORM_STATE,
  INITIAL_SNEAKERS_FIT_FEEDBACK_FORM_STATE,
} from 'productTemplate/state/fitFeedbackFormReducer';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { useFitFeedback } from 'productTemplate/hooks/useFitFeedback';
import { useFitFeedbackFormState } from 'productTemplate/providers/FitFeedbackProvider';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { useWindowSize } from 'shared/hooks/useWindowSize';
import { t } from 'localization/utils/t';
import { Window } from 'shared/components/Window';

const TP = 'productTemplate.components.fitFeedback.FitFeedbackFormWindow';

export const FitFeedbackFormWindow = () => {
  const { productCategory } = useStoreSelector((state) => state.pdp.productTemplate);
  const { errorMessage, handleCloseFitFeedbackForm, handleFitClick, handleFormSubmit } =
    useFitFeedback();
  const { heightMeasurement } = useFitFeedbackFormState();
  const {
    isMobile,
    windowSize: { width },
  } = useWindowSize();
  const [isScrollAtTop, setIsScrollAtTop] = useState(true);
  const formikFormRef = useRef<FormikProps<FormikValues>>(null);
  const isSneakers = productCategory === 'shoes';

  const handleScroll = (e) => {
    if (e.currentTarget.scrollTop === 0) {
      setIsScrollAtTop(true);
    } else {
      setIsScrollAtTop(false);
    }
  };

  const handleClose = () => {
    handleCloseFitFeedbackForm();
    handleFitClick();
  };

  const handleWheel = (e) => {
    e.stopPropagation();
  };

  useEffect(() => {
    sendTrackingEvent('MODAL_OPEN_VIEW', { modal_name: 'feedback_on_fit' });
  }, []);

  return (
    <FitFeedbackFormWindow.Window
      id="fit"
      isPermanent
      onClose={handleClose}
      onWheel={handleWheel}
      initialPermanentPosition={!isMobile && { bottom: 0, left: width - 365 }}
      title={t(`${TP}.formTitle`, 'Feedback on Fit')}
    >
      {!isScrollAtTop && <FitFeedbackFormWindow.HorizontalLine />}
      <FitFeedbackFormWindow.Body onScroll={handleScroll}>
        <FitFeedbackFormWindow.Instructions>
          {t(
            `${TP}.formInstructions`,
            'Your feedback is anonymous and helps inform our community of the fit of this item',
          )}
        </FitFeedbackFormWindow.Instructions>
        <Formik
          initialValues={
            isSneakers
              ? INITIAL_SNEAKERS_FIT_FEEDBACK_FORM_STATE
              : INITIAL_APPAREL_FIT_FEEDBACK_FORM_STATE
          }
          innerRef={formikFormRef}
          onSubmit={handleFormSubmit}
          validationSchema={getFitFeedbackFormValidationSchema(isSneakers, heightMeasurement)}
        >
          {({
            dirty,
            errors,
            handleBlur,
            handleSubmit,
            isSubmitting,
            isValid,
            setFieldValue,
            touched,
          }) => (
            <Form>
              {isSneakers ? (
                <FitFeedbackFormSneakers
                  setFieldValue={setFieldValue}
                  errors={errors}
                  touched={touched}
                />
              ) : (
                <>
                  <FitFeedbackFormUserInfo
                    errors={errors}
                    onBlur={handleBlur}
                    setFieldValue={setFieldValue}
                    touched={touched}
                  />
                  <FitFeedbackFormWindow.ApparelFormHorizontalLine />
                  <FitFeedbackFormApparel
                    setFieldValue={setFieldValue}
                    errors={errors}
                    touched={touched}
                  />
                </>
              )}
              <FitFeedbackFormWindow.SubmitButton
                data-qa="submit_fit_feedback"
                onClick={() => handleSubmit()}
                disabled={isSubmitting}
                $appearDisabled={!(isValid && dirty)}
              >
                {t(`${TP}.submitFeedback`, 'Submit Feedback')}
              </FitFeedbackFormWindow.SubmitButton>
            </Form>
          )}
        </Formik>
        {errorMessage && <FitFeedbackFormWindow.Error>{errorMessage}</FitFeedbackFormWindow.Error>}
      </FitFeedbackFormWindow.Body>
    </FitFeedbackFormWindow.Window>
  );
};

FitFeedbackFormWindow.Window = styled(Window)`
  max-height: 420px;
  z-index: ${({ theme }) => theme.zIndex.fitFeedbackWindow} !important;

  @media (max-width: ${({ theme }) => theme.breakpoints.small - 1}px) {
    max-height: 500px;
    margin-left: 16px;
    margin-right: 16px;
    max-width: ${({ theme }) => theme.breakpoints.small}px;
    margin-top: 10vh;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall - 1}px) {
    max-height: 500px;
    position: absolute;
    right: 66px;
    bottom: 0;
    margin-top: 10vh;
  }
`;

FitFeedbackFormWindow.Body = styled.div`
  padding: 0 16px 16px;
  overflow: auto;
`;

FitFeedbackFormWindow.HorizontalLine = styled.hr`
  margin: 0;
`;

FitFeedbackFormWindow.ApparelFormHorizontalLine = styled.hr`
  margin: 0 -16px;
`;

FitFeedbackFormWindow.Instructions = styled.p`
  margin-bottom: 32px;
`;

FitFeedbackFormWindow.SubmitButton = styled(Button)<{ $appearDisabled: boolean }>`
  cursor: pointer;

  ${({ $appearDisabled, theme }) =>
    $appearDisabled &&
    css`
      color: ${theme.colors.black} !important;
      cursor: default !important;
      border: ${theme.borders.black};
      background-color: ${theme.colors.white};
      &:focus {
        background-color: ${theme.colors.white};
      }
    `}
`;

FitFeedbackFormWindow.Error = styled.div`
  color: ${({ theme }) => theme.colors.red};
  text-align: center;
  margin: 8px 0;
`;
